import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ApprovalRequest } from "../types/legalOps";
import {
  listApprovalRequestsByModule,
  deleteApprovalRequest,
} from "../services/approvalRequestsService";
import { useToast } from "../components/Toast";
import { useAiContext } from "../ai/AiContext";
import { LegalListModule } from "../modules/legal/LegalListModule";
import { contractModuleConfig } from "../modules/legal/config/ContractModuleConfig";
import TemplatesPage from "./TemplatesPage"; 
import { ContractReviewForm } from "../modules/legal/forms/ContractReviewForm";
import { SignRequestDetailModal } from "../modules/sign/components/SignRequestDetailModal";
import {
  canInitiateLegalRequest,
  getDeptCodeByEmail,
  isLegal,
  isManager,
  isSuperAdmin,
  canViewAllData,
  isSuperUser,
} from "../config/roles";
import { getDeptNameByCode } from "../constants/departments";
import { ApprovalStatusBadge } from "../components/approval/ApprovalStatusBadge";
import { scanOrphanContractFlows, isFlowClosed } from "../services/contractFlowRescueReader";

export function ContractReviewsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"review" | "retention" | "templates">("review");
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);

  const canSeeRetention = isLegal(user?.email) || isManager(user?.email) || isSuperAdmin(user?.email);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="row" style={{ borderBottom: "1px solid #333", marginBottom: 16 }}>
        <button
          className="navbtn"
          style={{
            width: "auto",
            borderBottom: activeTab === "review" ? "2px solid #fff" : "none",
            borderRadius: 0,
            color: activeTab === "review" ? "#fff" : "var(--muted)",
          }}
          onClick={() => setActiveTab("review")}
        >
          合約審查 (送件與簽核)
        </button>
        {canSeeRetention && (
          <button
            className="navbtn"
            style={{
              width: "auto",
              borderBottom: activeTab === "retention" ? "2px solid #fff" : "none",
              borderRadius: 0,
              color: activeTab === "retention" ? "#fff" : "var(--muted)",
            }}
            onClick={() => setActiveTab("retention")}
          >
            合約留存管理 (資料庫)
          </button>
        )}
        <button
          className="navbtn"
          style={{
            width: "auto",
            borderBottom: activeTab === "templates" ? "2px solid #fff" : "none",
            borderRadius: 0,
            color: activeTab === "templates" ? "#fff" : "var(--muted)",
          }}
          onClick={() => setActiveTab("templates")}
        >
          制式合約範本庫
        </button>
      </div>

      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {activeTab === "review" && (
          <TabReviewSystem
            onSelectRequest={(req) => setSelectedRequest(req)}
          />
        )}
        {activeTab === "retention" && canSeeRetention && (
          <LegalListModule config={{ ...contractModuleConfig, canEdit: true }} />
        )}
        {activeTab === "templates" && <TemplatesPage />}
      </div>

      {selectedRequest && (
          <SignRequestDetailModal 
              request={selectedRequest}
              onClose={() => setSelectedRequest(null)}
              onUpdate={() => { setSelectedRequest(null); }}
          />
      )}
    </div>
  );
}

function TabReviewSystem({ onSelectRequest }: { onSelectRequest: (r: ApprovalRequest) => void }) {
  const { user, debugViewDepartmentId } = useAuth();
  const { showToast } = useToast();
  const { requestDelete } = useAiContext();
  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [subTab, setSubTab] = useState<'ACTIVE' | 'HISTORY'>('ACTIVE');

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    
    const isDeptAccount = user.role === 'dept-minimal';
    const email = user.email.toLowerCase();
    const effectiveDeptCode = debugViewDepartmentId || (isDeptAccount ? getDeptCodeByEmail(user.email) : null);
    
    // Strict Gating for Orphans: KC, AD, Lisa, Irene
    const isSuperAdminUser = ["kc@lg.app", "ad@lg.app", "lisa@lg.app", "irene@lg.app"].includes(email);

    const unsub = listApprovalRequestsByModule("contractDraft", async (all) => {
        let filtered = all;
        if (canViewAllData(user.email) && !debugViewDepartmentId) {
            filtered = all;
        } else if (effectiveDeptCode) {
            const deptName = getDeptNameByCode(effectiveDeptCode);
            filtered = all.filter((r) => r.department === deptName || r.visibleDepartments?.includes(deptName));
        } else {
            filtered = all.filter((r) => r.requesterUid === user.id);
        }

        // --- ORPHAN FLOW RECOVERY (KC/AD/LISA/IRENE ONLY) ---
        if (isSuperAdminUser && !debugViewDepartmentId) {
            const existingIds = new Set(all.map(a => a.id));
            const orphans = await scanOrphanContractFlows(existingIds);
            
            if (orphans.length > 0) {
                const rescuedItems: ApprovalRequest[] = orphans.map(o => ({
                    id: o.id,
                    module: "contractDraft",
                    title: o.title || `（合約資料缺失｜僅顯示簽核流程）`,
                    department: o.createdDept || "Unknown",
                    requesterName: o.assigneeName || "System Rescue",
                    status: o.status,
                    docCode: o.id, 
                    submittedAt: o.createdAt || null,
                    updatedAt: o.updatedAt || null,
                    approvalSummary: {
                        overallStatus: o.status,
                        currentStageLabel: "救援數據",
                    },
                    attachments: [],
                    priority: "normal",
                    sensitivity: "normal",
                    requesterUid: "rescue"
                } as any));
                
                filtered = [...filtered, ...rescuedItems];
                // Resort by date desc
                filtered.sort((a: any, b: any) => {
                    const tA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : (a.updatedAt ? new Date(a.updatedAt).getTime() : 0);
                    const tB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : (b.updatedAt ? new Date(b.updatedAt).getTime() : 0);
                    return tB - tA;
                });
            }
        }

        setItems(filtered);
        setLoading(false);
      });
    return () => { unsub.then((fn) => fn()); };
  }, [user, debugViewDepartmentId]);

  const handleDelete = (req: ApprovalRequest) => {
      requestDelete({
        targetType: "contractReview",
        targetId: req.id,
        summary: req.title,
        onConfirm: async () => {
            await deleteApprovalRequest(req.id);
            showToast("已刪除紀錄");
        }
      })
  };

  // Grouping logic based on Flow Status
  const activeList = items.filter(i => !isFlowClosed(i.status, i.approvalSummary?.currentStageKey));
  const historyList = items.filter(i => isFlowClosed(i.status, i.approvalSummary?.currentStageKey));
  const displayList = subTab === 'ACTIVE' ? activeList : historyList;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="row" style={{ gap: 0 }}>
                <button className={`navbtn ${subTab==='ACTIVE'?'active':''}`} onClick={() => setSubTab('ACTIVE')}>進行中 ({activeList.length})</button>
                <button className={`navbtn ${subTab==='HISTORY'?'active':''}`} onClick={() => setSubTab('HISTORY')}>歷史紀錄 ({historyList.length})</button>
            </div>
            {canInitiateLegalRequest(user?.role||'viewer') && (
                <button className="btn" style={{ width: "auto" }} onClick={() => setIsFormOpen(true)}>+ 提出合約審查</button>
            )}
        </div>

        <div style={{ flex: 1, overflow: "auto", background: "#161616", border: "1px solid var(--border)", borderRadius: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead style={{ background: "#222", color: "#aaa", position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                        <th style={{ padding: 12 }}>單號</th>
                        <th style={{ padding: 12 }}>標題</th>
                        <th style={{ padding: 12 }}>部門</th>
                        <th style={{ padding: 12 }}>當前狀態 / 關卡</th>
                        <th style={{ padding: 12 }}>最後更新</th>
                        <th style={{ padding: 12, textAlign: "right" }}>操作</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && <tr><td colSpan={6} style={{padding:40, textAlign:'center'}}>載入中...</td></tr>}
                    {!loading && displayList.map(item => {
                        const summary: any = item.approvalSummary || {};
                        const lastAction = summary.lastActionAt || item.updatedAt?.toDate?.().toISOString() || item.updatedAt;
                        const lastActionStr = lastAction ? new Date(lastAction).toLocaleString('zh-TW', {month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit'}) : '-';
                        const lastActor = summary.lastActorName || '';

                        return (
                            <tr key={item.id} style={{ borderBottom: "1px solid #333" }}>
                                <td style={{ padding: 12, color: "#a5b4fc" }}>{item.docCode || item.id.slice(0,8)}</td>
                                <td style={{ padding: 12, fontWeight: 500 }}>{item.title}</td>
                                <td style={{ padding: 12 }}>{item.department} <span style={{color:'#888'}}>{(item as any).handlerName || item.requesterName}</span></td>
                                <td style={{ padding: 12 }}>
                                    <ApprovalStatusBadge 
                                        module={item.module as string} 
                                        docId={item.id} 
                                        docCode={item.docCode}
                                        initialStatus={item.status}
                                        approvalSummary={item.approvalSummary}
                                    />
                                </td>
                                <td style={{ padding: 12, color: '#aaa', fontSize: 12 }}>
                                    {lastActionStr}
                                    {lastActor && <div style={{fontSize:10, opacity:0.7}}>by {lastActor}</div>}
                                </td>
                                <td style={{ padding: 12, textAlign: "right" }}>
                                    <button className="smallbtn" onClick={() => onSelectRequest(item)}>檢視</button>
                                    {(isSuperUser(user?.email) || item.status === 'DRAFT') && (
                                        <button className="smallbtn" style={{ marginLeft: 8, color: "var(--danger)", borderColor: "var(--danger)" }} onClick={() => handleDelete(item)}>刪除</button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {!loading && displayList.length === 0 && (
                        <tr><td colSpan={6} style={{padding: 40, textAlign: 'center', color: 'var(--muted)'}}>無資料</td></tr>
                    )}
                </tbody>
            </table>
        </div>

        {isFormOpen && (
            <ContractReviewForm 
                initialData={null} 
                onClose={() => setIsFormOpen(false)} 
                onSuccess={() => { setIsFormOpen(false); showToast("儲存成功"); }} 
                allExisting={items}
            />
        )}
    </div>
  );
}
